import { toISODate } from "../../utils/convert-fields"
import { createInitialValidationParameters } from "./validation-contract"
import { periodicidadeReverseMap } from "../EventosOrdinarios/components/eventos-ordinarios/map-eventos-ordinarios"

const NUMERIC_FIELDS = new Set([
    "IDCaracteristica", "VersaoDocumento", "Incentivada", "SpreadEmissao", "PUEmissao",
    "PUPARReferencia", "PUMTMReferencia", "SpreadCredito", "ValorNominal", "IDJuros",
    "IDFluxoEvento", "LiquidezDiaria", "TaxaJuros", "Defasagem", "IndexadorCorrecaoPercentual",
    "PeriodicidadeAtualizacaoMonetaria", "ProjecaoInflacao", "InflacaoNegativa",
    "AmortizacaoOrdinariaPercentual", "JurosIncorporacaoProgramadaPercentual", "CasasFatorJuros",
    "CasasJuros", "CasasFatorCorrecao", "CasasCorrecao", "CasasFatorJurosCDI", "CasasVNA",
    "CasasFatorDesconto", "CasasFatorExpectativa", "CasasPU", "FormulaIndexador",
    "RecalculoProjecao", "JurosCapitalizacao", "ValorPercentual", "ValorFinanceiro", "FrequenciaJuros",
    "FrequenciaAmortizacao", "FrequenciaRecompra", "DiaAniversario", "AniversarioPeriodico",
])

const EMPTY_CALCULATION = { status: "idle", viewModel: {} }
const isBlank = (value) => value === "" || value === null || value === undefined
const dateValue = (value) => (isBlank(value) ? null : toISODate(value))
const dateValueIfPresent = (value) => (value === undefined ? undefined : dateValue(value))

const normalizeLocalizedDecimal = (value) => {
    if (typeof value === "number") return Number.isNaN(value) ? null : value
    if (typeof value !== "string") return value

    const text = value.trim()
    if (!text) return null
    if (!/^-?[\d.,]+$/.test(text)) return value

    const comma = text.lastIndexOf(",")
    const dot = text.lastIndexOf(".")
    const normalized = comma > dot
        ? text.replace(/\./g, "").replace(",", ".")
        : dot > comma && comma >= 0
            ? text.replace(/,/g, "")
            : text.replace(",", ".")
    return normalized
}

export const mapIncentivada = (value) => {
    if (value === 1 || value === "1") return 1
    if (value === 0 || value === "0") return 0

    const normalized = typeof value === "string" ? value.trim().toLowerCase() : value
    if (normalized === "sim") return 1
    if (normalized === "nao" || normalized === "não") return 0
    return null
}

const normalizeValue = (value, fieldName) => {
    if (value === "") return null
    if (fieldName === "PeriodicidadeAtualizacaoMonetaria") {
        return periodicidadeReverseMap[value] ?? (/^[1-6]$/.test(String(value)) ? Number(value) : value)
    }
    return NUMERIC_FIELDS.has(fieldName) ? normalizeLocalizedDecimal(value) : value
}

const compact = (value) => Object.entries(value).reduce((result, [key, item]) => {
    if (item === undefined) return result
    return { ...result, [key]: normalizeValue(item, key) }
}, {})

const hasRowData = (row) => Object.values(row || {}).some((value) => !isBlank(value))
const mapRows = (rows, mapper) => (Array.isArray(rows) ? rows : [])
    .filter(hasRowData)
    .map(mapper)
    .filter(hasRowData)
const getDraftValue = (row, draftKey, contractKey = undefined) => (
    row?.[draftKey] ?? row?.[contractKey]
)

const EVENTO_ORDINARIO_FIELDS = [
    ["idJuros", "IDJuros"], ["idFluxoEvento", "IDFluxoEvento"], ["indexador", "Indexador"],
    ["liquidezDiaria", "LiquidezDiaria"], ["valorNominal", "ValorNominal"], ["taxaJuros", "TaxaJuros"],
    ["defasagem", "Defasagem"], ["indexadorCorrecaoPercentual", "IndexadorCorrecaoPercentual"],
    ["periodicidadeAtualizacaoMonetaria", "PeriodicidadeAtualizacaoMonetaria"],
    ["projecaoInflacao", "ProjecaoInflacao"], ["inflacaoNegativa", "InflacaoNegativa"],
    ["aplicacaoAmortizacao", "AplicacaoAmortizacao"],
    ["amortizacaoOrdinariaPercentual", "AmortizacaoOrdinariaPercentual"],
    ["jurosIncorporacaoProgramadaPercentual", "JurosIncorporacaoProgramadaPercentual"],
    ["calculoFatorJuros", "CalculoFatorJuros"], ["casasFatorJuros", "CasasFatorJuros"],
    ["calculoJuros", "CalculoJuros"], ["casasJuros", "CasasJuros"],
    ["calculoFatorCorrecao", "CalculoFatorCorrecao"], ["casasFatorCorrecao", "CasasFatorCorrecao"],
    ["calculoCorrecao", "CalculoCorrecao"], ["casasCorrecao", "CasasCorrecao"],
    ["calculoFatorJurosCDI", "CalculoFatorJurosCDI"], ["casasFatorJurosCDI", "CasasFatorJurosCDI"],
    ["calculoVNA", "CalculoVNA"], ["casasVNA", "CasasVNA"],
    ["calculoFatorDesconto", "CalculoFatorDesconto"], ["casasFatorDesconto", "CasasFatorDesconto"],
    ["calculoFatorExpectativa", "CalculoFatorExpectativa"],
    ["casasFatorExpectativa", "CasasFatorExpectativa"],
    ["calculoPU", "CalculoPU"], ["casasPU", "CasasPU"], ["calculoDias", "CalculoDias"],
    ["formulaIndexador", "FormulaIndexador"],
    ["mesAniversarioAnualSemestral", "MesAniversarioAnualSemestral"],
    ["recalculoProjecao", "RecalculoProjecao"],
]
const EVENTO_ORDINARIO_DATE_FIELDS = [
    ["dataEvento", "DataEvento"], ["dataReferencia", "DataReferencia"], ["dataEventoEfetiva", "DataEventoEfetiva"], ["dataLiquidacao", "DataLiquidacao"], ["dataRentabilidadeInicio", "DataRentabilidadeInicio"], ["dataRentabilidadeFim", "DataRentabilidadeFim"], ["dataAniversarioJuros", "DataAniversarioJuros"],
]

const mapEventosOrdinariosSnapshot = (rows) => mapRows(rows, (row) => compact({
    ...EVENTO_ORDINARIO_FIELDS.reduce((result, [draftKey, contractKey]) => ({
        ...result,
        [contractKey]: contractKey === "IDJuros"
            ? getDraftValue(row, draftKey, contractKey) ?? row.JurosCapitalizacao
            : getDraftValue(row, draftKey, contractKey),
    }), {}),
    ...EVENTO_ORDINARIO_DATE_FIELDS.reduce((result, [draftKey, contractKey]) => ({
        ...result,
        [contractKey]: dateValueIfPresent(getDraftValue(row, draftKey, contractKey)),
    }), {}),
}))

const mapEventosExtraordinariosSnapshot = (rows) => mapRows(rows, (row) => compact({
    DataReferencia: dateValueIfPresent(getDraftValue(row, "dataReferencia", "DataReferencia")),
    DataEvento: dateValueIfPresent(getDraftValue(row, "dataEvento", "DataEvento")),
    DataLiquidacao: dateValueIfPresent(getDraftValue(row, "dataLiquidacao", "DataLiquidacao")),
    TipoEvento: getDraftValue(row, "tipoEvento", "TipoEvento"),
    ValorPercentual: getDraftValue(row, "valorPercentual", "ValorPercentual"),
    ValorFinanceiro: getDraftValue(row, "valorFinanceiro", "ValorFinanceiro"),
    Fonte: getDraftValue(row, "fonte", "Fonte"),
}))

const mapRecomprasProgramadasSnapshot = (rows) => mapRows(rows, (row) => compact({
    DataReferencia: dateValueIfPresent(getDraftValue(row, "dataReferencia", "DataReferencia")),
    DataRecompraProgramada: dateValueIfPresent(getDraftValue(row, "dataRecompraProgramada", "DataRecompraProgramada")),
}))

const mapDatasAniversarioSnapshot = (rows) => mapRows(rows, (row) => compact({
    IDFluxoEvento: getDraftValue(row, "idFluxoEvento", "IDFluxoEvento"),
    DataAniversario: dateValueIfPresent(getDraftValue(row, "dataAniversario", "DataAniversario")),
    DataAniversarioAnterior: dateValueIfPresent(getDraftValue(row, "dataAniversarioAnterior", "DataAniversarioAnterior")),
    DataReferencia: dateValueIfPresent(getDraftValue(row, "dataReferencia", "DataReferencia")),
    CorrigeDataAniversario: getDraftValue(row, "corrigeDataAniversario", "CorrigeDataAniversario"),
    Indexador: getDraftValue(row, "indexador", "Indexador"),
    AniversarioPeriodico: getDraftValue(row, "aniversarioPeriodico", "AniversarioPeriodico"),
}))

const mapCaracteristica = (source = {}, context = {}) => compact({
    CETIP: source.cetip ?? context.cetip ?? null,
    VersaoDocumento: source.versaoDocumento ?? context.versaoDocumento ?? null,
    TipoAtivo: source.tipoAtivo ?? null,
    Indexador: source.indexador ?? null,
    Incentivada: mapIncentivada(source.incentivada),
    SpreadEmissao: source.spreadEmissao,
    IDEmissao: source.idEmissao,
    DataReferencia: dateValueIfPresent(source.dataReferencia),
    DataEmissao: dateValueIfPresent(source.dataEmissao),
    DataVencimento: dateValueIfPresent(source.dataVencimento),
    PUEmissao: source.puEmissao,
})

const mapParametros = (source = {}) => {
    const caracteristicas = source.caracteristicas ?? {}
    const datas = source.datas ?? {}
    const juros = source.jurosCorrecao ?? {}

    return compact({
        DataReferencia: dateValueIfPresent(caracteristicas.dataReferencia),
        TaxaJuros: caracteristicas.taxaJuros,
        TipoAtivo: caracteristicas.tipoAtivo,
        Indexador: caracteristicas.indexador,
        LiquidezDiaria: caracteristicas.liquidezDiaria,
        ValorNominal: datas.valorNominal,
        DataEmissao: dateValueIfPresent(datas.dataEmissao),
        DataPrimeiraIntegralizacao: dateValueIfPresent(datas.dataIntegralizacao),
        DataVencimento: dateValueIfPresent(datas.dataVencimento),
        DataRentabilidadeInicio: dateValueIfPresent(datas.dataRentabilidadeInicio),
        DataRentabilidadeFim: dateValueIfPresent(datas.dataRentabilidadeFim),
        FrequenciaJuros: datas.frequenciaJuros,
        FrequenciaAmortizacao: datas.frequenciaAmortizacao,
        DataInicioPagamentoJuros: dateValueIfPresent(datas.dataInicioPagamentoJuros),
        DataInicioPagamentoAmortizacao: dateValueIfPresent(datas.dataInicioPagamentoAmortizacao),
        DataCarenciaRecompra: dateValueIfPresent(datas.dataCarenciaRecompra),
        FrequenciaRecompra: datas.frequenciaRecompra,
        IndexadorCorrecaoPercentual: juros.indexadorCorrecaoPercentual,
        Defasagem: juros.defasagem,
        FormulaIndexador: juros.formulaIndexador,
        JurosCapitalizacao: juros.jurosCapitalizacao,
        PeriodicidadeAtualizacaoMonetaria: juros.periodicidadeAtualizacaoMonetaria,
        MesAniversarioAnualSemestral: juros.mesAniversarioAnualSemestral,
        ProjecaoInflacao: juros.projecaoInflacao,
        InflacaoNegativa: juros.inflacaoNegativa,
        RecalculoProjecao: juros.recalculoProjecao,
        AplicacaoAmortizacao: juros.aplicacaoAmortizacao,
        CorrigeDataEvento: juros.corrigeDataEvento,
        CorrigeDataAniversario: juros.corrigeDataAniversario,
        TipoCorrecao: juros.tipoCorrecao,
        DiaAniversario: juros.diaAniversario,
        CalculoFatorJuros: source.fatorArredondamento?.calculoFatorJuros,
        CasasFatorJuros: source.fatorArredondamento?.casasFatorJuros,
        CalculoJuros: source.fatorArredondamento?.calculoJuros,
        CasasJuros: source.fatorArredondamento?.casasJuros,
        CalculoFatorCorrecao: source.fatorArredondamento?.calculoFatorCorrecao,
        CasasFatorCorrecao: source.fatorArredondamento?.casasFatorCorrecao,
        CalculoCorrecao: source.fatorArredondamento?.calculoCorrecao,
        CasasCorrecao: source.fatorArredondamento?.casasCorrecao,
        CalculoFatorJurosCDI: source.fatorArredondamento?.calculoFatorJurosCDI,
        CasasFatorJurosCDI: source.fatorArredondamento?.casasFatorJurosCDI,
        CalculoVNA: source.fatorArredondamento?.calculoVNA,
        CasasVNA: source.fatorArredondamento?.casasVNA,
        CalculoFatorDesconto: source.fatorArredondamento?.calculoFatorDesconto,
        CasasFatorDesconto: source.fatorArredondamento?.casasFatorDesconto,
        CalculoFatorExpectativa: source.fatorArredondamento?.calculoFatorExpectativa,
        CasasFatorExpectativa: source.fatorArredondamento?.casasFatorExpectativa,
        CalculoPU: source.fatorArredondamento?.calculoPU,
        CasasPU: source.fatorArredondamento?.casasPU,
    })
}

const mapParametrosValidacao = (parameters = createInitialValidationParameters()) => ({
    DataReferencia: parameters.dataReferencia
        ? (
            /^\d{4}-\d{2}-\d{2}$/.test(parameters.dataReferencia)
                ? parameters.dataReferencia
                : dateValue(parameters.dataReferencia)
        )
        : null,
    PUPARReferencia: normalizeValue(parameters.puparReferencia, "PUPARReferencia"),
    PUMTMReferencia: normalizeValue(parameters.pumtmReferencia, "PUMTMReferencia"),
    SpreadCredito: normalizeValue(parameters.spreadCredito, "SpreadCredito"),
})

export const createInitialValidationSnapshot = () => ({
    idCaracteristica: null,
    caracteristica: {},
    parametros: {},
    eventosOrdinarios: [],
    eventosExtraordinarios: [],
    recomprasProgramadas: [],
    datasAniversario: [],
})

export const mapValidationSnapshot = (
    draft = {},
    context = {},
    validationParameters = createInitialValidationParameters(),
) => ({
    IDCaracteristica: normalizeValue(draft.idCaracteristica ?? context.idCaracteristica, "IDCaracteristica"),
    ParametrosValidacao: mapParametrosValidacao(validationParameters),
    Caracteristica: mapCaracteristica(draft.caracteristica, context),
    Parametros: mapParametros(draft.parametros),
    EventosOrdinarios: mapEventosOrdinariosSnapshot(draft.eventosOrdinarios),
    EventosExtraordinarios: mapEventosExtraordinariosSnapshot(draft.eventosExtraordinarios),
    RecomprasProgramadas: mapRecomprasProgramadasSnapshot(draft.recomprasProgramadas),
    DatasAniversario: mapDatasAniversarioSnapshot(draft.datasAniversario),
    // O Core exige as listas, mas a prévia por Taxa ainda não calcula prêmios.
    Premios: [],
    Conversibilidades: [],
})

export const createInitialValidationState = (snapshotRevision = 0) => ({
    snapshot: createInitialValidationSnapshot(),
    calculation: EMPTY_CALCULATION,
    snapshotRevision,
})

export const validationSnapshotReducer = (state, action) => {
    if (action.type === "CALCULATION_LOADING") return { ...state, calculation: { status: "loading", identity: action.identity, viewModel: {} } }
    if (action.type === "CALCULATION_SUCCESS") return { ...state, calculation: { status: "success", identity: action.identity, viewModel: action.viewModel } }
    if (action.type === "CALCULATION_ERROR") return { ...state, calculation: { status: "error", identity: action.identity, viewModel: {} } }
    if (action.type === "RESET") {
        const hasCurrentState = state.snapshotRevision > 0
            || state.calculation.status !== EMPTY_CALCULATION.status
        return createInitialValidationState(
            state.snapshotRevision + (hasCurrentState ? 1 : 0),
        )
    }
    if (action.type === "INVALIDATE_RESULTS") {
        return {
            ...state,
            calculation: EMPTY_CALCULATION,
            snapshotRevision: state.snapshotRevision + 1,
        }
    }
    if (action.type === "HYDRATE_DRAFT") {
        const snapshot = { ...state.snapshot, ...action.draft }
        if (JSON.stringify(snapshot) === JSON.stringify(state.snapshot)) return state
        return {
            ...state,
            snapshot,
            calculation: EMPTY_CALCULATION,
            snapshotRevision: state.snapshotRevision + 1,
        }
    }
    if (action.type !== "UPDATE_DRAFT") return state
    return {
        ...state,
        snapshot: action.draft
            ? { ...state.snapshot, ...action.draft }
            : state.snapshot,
        calculation: EMPTY_CALCULATION,
        snapshotRevision: state.snapshotRevision + 1,
    }
}
